import { Modal as ModalAD } from 'antd';
import cs from 'classnames';
import type { ComponentProps } from 'react';
import { ModalFullWidth, type ModalFullWidthProperties } from './modal-full-width';
import { ModalHugContent, type ModalHugContentProperties } from './modal-hug-content';
import { ModalScrollableContent, type ModalScrollableContentProperties } from './modal-scrollable-content';
import { ModalTitle } from './modal-title';
import { type ModalWidth, modalWidths } from './modal-widths';
import styles from './modal.module.css';
import { useCustomModal } from './useCustomModal';

export type ModalOptions<
  OpenedOptions extends Record<string, unknown>,
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type -- Prefer {} over Record<string, unknown> to ensure that properties from the `isOpened: true` variant aren't accessible when isOpened is false
  ClosedOptions extends Record<string, unknown> = {},
> = ({ isOpened: false } & ClosedOptions) | ({ isOpened: true } & OpenedOptions);

export type ModalProperties = {
  isOpened: boolean;
  visuallyHidden?: boolean;
  width?: ModalWidth;
} & Omit<ComponentProps<typeof ModalAD>, 'width'>;

const Modal = ({
  children,
  className,
  classNames,
  closable = true,
  destroyOnHidden = true,
  focusable,
  footer = null,
  isOpened,
  maskClosable = true,
  onCancel,
  title,
  visuallyHidden,
  width = 'small',
  ...rest
}: ModalProperties) => (
  <ModalAD
    centered
    className={cs(styles.modal, className, {
      'rc-visually-hidden': visuallyHidden,
    })}
    classNames={
      typeof classNames === 'function'
        ? (info) => {
            const resolvedClassNames = classNames(info);

            return {
              ...resolvedClassNames,
              body: cs(styles.body, resolvedClassNames?.body),
              container: cs(styles.container, resolvedClassNames?.container),
              header: cs(styles.header, resolvedClassNames?.header),
            };
          }
        : {
            ...classNames,
            body: cs(styles.body, classNames?.body),
            container: cs(styles.container, classNames?.container),
            header: cs(styles.header, classNames?.header),
          }
    }
    closable={closable}
    destroyOnHidden={destroyOnHidden}
    focusable={{ trap: false, ...focusable }}
    footer={footer}
    maskClosable={maskClosable}
    onCancel={onCancel}
    open={isOpened}
    title={<ModalTitle closable={closable} onCancel={onCancel} title={title} />}
    width={typeof width === 'number' ? width : modalWidths[width]}
    {...rest}
  >
    {children}
  </ModalAD>
);

Modal.useModal = useCustomModal;
Modal.ScrollableContent = Object.assign(
  function ({ className, ...rest }: ModalScrollableContentProperties) {
    return <ModalScrollableContent className={cs(className, styles['scrollable-content'])} {...rest} />;
  },
  {
    Footer: ModalScrollableContent.Footer,
    Header: ModalScrollableContent.Header,
  },
);

Modal.HugContent = Object.assign(
  function ({ className, ...rest }: ModalHugContentProperties) {
    return <ModalHugContent className={cs(className, styles['hug-content'])} {...rest} />;
  },
  {
    Footer: ModalHugContent.Footer,
  },
);

Modal.FullWidth = function ({ children, className, fullWidthDisabled }: ModalFullWidthProperties) {
  return (
    <ModalFullWidth className={cs(className, { [styles['full-width-content'] ?? '']: !fullWidthDisabled })}>
      {children}
    </ModalFullWidth>
  );
};

export default Modal;
